import Dialog from '@mui/material/Dialog';
import React, { useMemo } from 'react';
import { GoalProgressHistoryForm } from 'shared/components/ui/goal-progress-history-form';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { MetricEntry, MetricUnit } from 'shared/types/goal-metric';
import { GoalProgressHistoryFormFields } from 'shared/types/goal-progress-form';
import { WeekDays } from 'shared/types/week-days';

import { GoalProgressHistoryError } from './goal-progress-history-error';
import { GoalProgressHistoryLoading } from './goal-progress-history-loading';

export type GoalProgressDialogProps = {
  open?: boolean;
  initialValues?: GoalProgressHistoryFormFields;
  unit?: MetricUnit;
  target?: number;
  startValue?: number;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  onClose: () => void;
  onSubmit: (entries: GoalProgressHistoryFormFields['entries']) => void;
  onAdd?: () => void;
  onRetry: () => void;
  isLoading?: boolean;
  isError?: boolean;
};

const defaultInitialValues: { entries: MetricEntry[] } = {
  entries: [],
};

export const GoalProgressHistoryDialog: React.FunctionComponent<
  GoalProgressDialogProps
> = ({
  initialValues = defaultInitialValues,
  unit,
  target,
  startValue,
  dateFormat,
  weekStartsOn,
  open,
  onClose,
  onSubmit,
  onAdd,
  onRetry,
  isLoading,
  isError,
}) => {
  const content = useMemo(() => {
    if (isError) {
      return <GoalProgressHistoryError onRetry={onRetry} onCancel={onClose} />;
    }

    if (isLoading) {
      return <GoalProgressHistoryLoading />;
    }

    return (
      <GoalProgressHistoryForm
        entries={initialValues.entries}
        unit={unit}
        target={target}
        startValue={startValue}
        dateFormat={dateFormat}
        weekStartsOn={weekStartsOn}
        onSubmit={onSubmit}
        onClose={onClose}
        onAdd={onAdd}
      />
    );
  }, [
    isError,
    isLoading,
    initialValues.entries,
    unit,
    target,
    startValue,
    dateFormat,
    weekStartsOn,
    onSubmit,
    onClose,
    onAdd,
    onRetry,
  ]);

  return (
    <Dialog open={!!open} fullWidth>
      {content}
    </Dialog>
  );
};
